import { store } from "./state";
import { dailyReminder, dayKey, NO_EVENT_TEST, reachedReminder, wholeDaysLeft, type ReminderMessage } from "./copy/reminders";

export function isNotificationSupported(): boolean {
  return "Notification" in window;
}

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * Where notifications stand on this device, for Settings to explain.
 * iOS only offers web notifications to apps added to the Home Screen
 * (iOS 16.4+), so in a Safari tab the answer is "install", not "no".
 */
export type NotificationReadiness = "granted" | "default" | "denied" | "install" | "unsupported";

/** "9am", "Midday", "6pm": how reminder times read everywhere. */
export function hourLabel(h: number): string {
  if (h === 0) return "Midnight";
  if (h === 12) return "Midday";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

export function notificationReadiness(): NotificationReadiness {
  if (!isNotificationSupported()) return isIOS() && !isStandalone() ? "install" : "unsupported";
  if (isIOS() && !isStandalone()) return "install";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  const result = await Notification.requestPermission();
  return result;
}

/** The service worker registration, if one turns up within a couple of seconds. */
async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!("serviceWorker" in navigator)) return undefined;
  const timeout = new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), 2500));
  return Promise.race([navigator.serviceWorker.ready.catch(() => undefined), timeout]);
}

/** Shows a notification from the page. Resolves true if the system accepted it. */
async function fire(msg: ReminderMessage, tag: string): Promise<boolean> {
  if (!isNotificationSupported() || Notification.permission !== "granted") return false;
  const options: NotificationOptions = { body: msg.body, tag, icon: "/icons/icon-192.png", badge: "/icons/icon-192.png" };
  const reg = await registration();
  if (reg) {
    try {
      await reg.showNotification(msg.title, options);
      return true;
    } catch {
      // fall through to the page-level constructor
    }
  }
  try {
    new Notification(msg.title, options);
    return true;
  } catch {
    // iOS only shows notifications through the service worker above.
    return false;
  }
}

/** Today's reminder for the current countdown, or null with no countdown. */
function todaysMessage(): ReminderMessage | null {
  const state = store.get();
  const event = store.getPrimaryEvent();
  if (!event) return null;
  if (new Date(event.targetDate).getTime() <= Date.now()) return reachedReminder(event);
  return dailyReminder(event, state.censorEnabled);
}

// ---------- push ----------
//
// With the server set up (server/push.ts), the phone subscribes once and the
// server sends the daily reminder at the chosen hour, even with the app
// closed. Without it (plain `npm run dev`, the lab), the app falls back to
// reminding the first time it's opened each day.

export const DEFAULT_HOUR = 9;

let keyRequest: Promise<string | null> | null = null;

/** The server's VAPID public key, or null if this deployment has no push server. */
export function pushServerKey(): Promise<string | null> {
  keyRequest ??= fetch("/api/push/key", { cache: "no-store" })
    .then(async (res) => (res.ok ? ((await res.json()) as { publicKey?: string }).publicKey ?? null : null))
    .catch(() => null)
    .then((key) => {
      if (!key) keyRequest = null; // try again next time
      return key;
    });
  return keyRequest;
}

function pushCapable(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && isNotificationSupported();
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((b64url.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** What the server needs to write this phone's reminders. */
function pushPrefs() {
  const state = store.get();
  const event = store.getPrimaryEvent();
  return {
    event: event ? { id: event.id, name: event.name, personName: event.personName, targetDate: event.targetDate } : null,
    level: state.profanityLevel,
    censor: state.censorEnabled,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    hour: state.notifications.hour ?? DEFAULT_HOUR,
    badge: state.notifications.badge !== false,
  };
}

async function post(path: string, body: unknown): Promise<Response | null> {
  try {
    return await fetch(`/api/push/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    return null;
  }
}

function b64url(buf: ArrayBuffer | null | undefined): string {
  if (!buf) return "";
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * The subscription for `key`, making one if needed (the permission must
 * already be granted). One made with a different key is useless, since the
 * push service only accepts pushes signed by the key it was made with, so
 * it's dropped and replaced.
 */
async function subscription(key: string): Promise<PushSubscription | null> {
  const reg = await registration();
  if (!reg || Notification.permission !== "granted") return null;
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      const usedKey = b64url(existing.options?.applicationServerKey);
      if (!usedKey || usedKey === key) return existing;
      await post("unsubscribe", { endpoint: existing.endpoint });
      await existing.unsubscribe().catch(() => false);
    }
    return await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
  } catch {
    return null;
  }
}

let lastSynced = "";

/** Sends the current settings to the server. Resolves true if push is live on this device. */
export async function syncPush(force = false, retry = true): Promise<boolean> {
  if (!pushCapable() || notificationReadiness() !== "granted") return false;
  const key = await pushServerKey();
  if (!key) return false;
  const sub = await subscription(key);
  if (!sub) return false;
  const prefs = pushPrefs();
  const signature = JSON.stringify([sub.endpoint, key, prefs]);
  if (!force && signature === lastSynced) return true;
  const res = await post("subscribe", { subscription: sub.toJSON(), key, prefs });
  if (res?.status === 409 && retry) {
    // The server can't sign for the key this phone subscribed with: start over with its current key.
    keyRequest = null;
    await sub.unsubscribe().catch(() => false);
    return syncPush(true, false);
  }
  if (!res?.ok) return false;
  lastSynced = signature;
  return true;
}

/**
 * Turns daily reminders on. Must run from a tap: it asks for permission
 * first, then subscribes to push if the server is there.
 */
export async function enableReminders(): Promise<NotificationPermission | "unsupported" | "install"> {
  const readiness = notificationReadiness();
  if (readiness === "install" || readiness === "unsupported") return readiness;
  const permission = await requestNotificationPermission();
  if (permission !== "granted") {
    store.setNotificationPrefs({ enabled: false, push: false, lastPermission: permission });
    return permission;
  }
  const push = await syncPush(true);
  store.setNotificationPrefs({ enabled: true, push, lastPermission: permission });
  return permission;
}

export async function disableReminders(): Promise<void> {
  store.setNotificationPrefs({ enabled: false, push: false });
  lastSynced = "";
  const reg = await registration();
  const sub = await reg?.pushManager?.getSubscription().catch(() => null);
  if (!sub) return;
  await post("unsubscribe", { endpoint: sub.endpoint });
  await sub.unsubscribe().catch(() => false);
  void updateBadge();
}

export type TestResult = "sent" | "scheduled" | "busy" | "denied" | "install" | "unsupported" | "failed" | "server-failed";

/**
 * Settings' test button: today's real reminder. With push live it comes
 * from the server, the same way the daily one will (and with `delay` it
 * waits long enough to lock the phone); otherwise the app shows it itself.
 * `detail` says what the push service answered when it goes wrong.
 */
export async function sendTestNotification(delay = 0): Promise<{ result: TestResult; detail?: string }> {
  const readiness = notificationReadiness();
  if (readiness === "install" || readiness === "unsupported") return { result: readiness };
  const permission = await requestNotificationPermission();
  if (permission !== "granted") return { result: permission === "unsupported" ? "unsupported" : "denied" };

  const state = store.get();
  if (state.notifications.enabled && (await syncPush())) {
    type Answer = { gone?: boolean; status?: number; detail?: string; error?: string };
    const attempt = async () => {
      const sub = await (await registration())?.pushManager.getSubscription();
      const res = sub ? await post("test", { endpoint: sub.endpoint, delay }) : null;
      return { sub, res, body: ((await res?.json().catch(() => null)) ?? {}) as Answer };
    };
    let { sub, res, body } = await attempt();
    // The server forgot this phone (404), can't sign for its key (409), or
    // Apple says the subscription is dead (gone): subscribe afresh, once.
    if (res && !res.ok && (res.status === 404 || res.status === 409 || body.gone)) {
      if (res.status === 409) keyRequest = null;
      if (res.status === 409 || body.gone) await sub?.unsubscribe().catch(() => false);
      if (await syncPush(true)) ({ res, body } = await attempt());
    }
    if (res?.ok) return { result: delay ? "scheduled" : "sent" };
    if (res?.status === 429) return { result: "busy" };
    const reason = (() => {
      try {
        return (JSON.parse(body.detail ?? "") as { reason?: string }).reason;
      } catch {
        return body.detail || body.error;
      }
    })();
    const detail = !res ? "No answer from the server." : body.status ? `Apple said ${body.status}${reason ? ` (${reason})` : ""}.` : `Server said ${res.status}${reason ? ` (${reason})` : ""}.`;
    return { result: "server-failed", detail };
  }
  return { result: (await fire(todaysMessage() ?? NO_EVENT_TEST, "cuntdown-test")) ? "sent" : "failed" };
}

// ---------- the Home Screen badge ----------

type BadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

/** Days left on the app icon (iOS shows it once notifications are allowed). */
export async function updateBadge(): Promise<void> {
  const nav = navigator as BadgeNavigator;
  if (!nav.setAppBadge || !nav.clearAppBadge) return;
  const state = store.get();
  const event = store.getPrimaryEvent();
  const want = state.notifications.enabled && state.notifications.badge !== false && notificationReadiness() === "granted";
  const days = event ? wholeDaysLeft(event.targetDate) : 0;
  try {
    if (want && days > 0) await nav.setAppBadge(days);
    else await nav.clearAppBadge();
  } catch {
    // badges are a nicety
  }
}

// ---------- keeping everything current ----------

/**
 * Keeps reminders going while the app runs:
 * - With push live, re-sends the settings to the server whenever the
 *   countdown, rudeness, censor or reminder time change (and on every
 *   open, which also re-subscribes if iOS dropped the subscription).
 * - Without push, fires the day's reminder itself, the first time the app
 *   is open that day, plus once more when the date arrives. It can't wake
 *   a closed app, which is what push is for.
 */
export function startNotificationWatcher(): () => void {
  let syncTimer = 0;
  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(async () => {
      const n = store.get().notifications;
      if (!n.enabled) return;
      const live = await syncPush();
      // Offline isn't "no server": don't drop to in-app reminders (and double up) over a dropped connection.
      if (live !== !!n.push && (live || navigator.onLine)) store.setNotificationPrefs({ push: live });
    }, 800);
  };

  const check = () => {
    const state = store.get();
    void updateBadge();
    if (!state.notifications.enabled || notificationReadiness() !== "granted") return;
    if (state.notifications.push) return; // the server has it
    const event = store.getPrimaryEvent();
    if (!event) return;

    if (new Date(event.targetDate).getTime() <= Date.now()) {
      if (!state.notifications.reachedNotified) {
        store.setNotificationPrefs({ reachedNotified: true });
        void fire(reachedReminder(event), "cuntdown-reached");
      }
      return;
    }

    const todayKey = dayKey(new Date());
    if (state.notifications.lastNotifiedDayKey === todayKey) return;
    store.setNotificationPrefs({ lastNotifiedDayKey: todayKey });
    void fire(dailyReminder(event, state.censorEnabled), "cuntdown-daily");
  };

  // A backgrounded PWA's timers are frozen, so also check the moment it
  // comes back to the foreground rather than up to a minute later.
  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    check();
    scheduleSync();
  };
  const unsubscribe = store.subscribe(scheduleSync);
  check();
  scheduleSync();
  const id = window.setInterval(check, 60_000);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.clearInterval(id);
    window.clearTimeout(syncTimer);
    unsubscribe();
    document.removeEventListener("visibilitychange", onVisible);
  };
}
